import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as flags from "country-flag-icons/react/3x2";
import {
  getCountryCallingCode,
  getCountries,
  isValidPhoneNumber,
  AsYouType,
} from "libphonenumber-js";
import { Search, ChevronDown } from "lucide-react";

export function PhoneField({
  phone,
  country,
  error,
  label,
  required = true,
  disabled = false,
  placeholder,
  className = "",
  containerHeight = "h-12",
  autoFocus = false,
  onKeyDown,
  onPhoneChange,
  onCountryChange,
}) {
  const { t, i18n } = useTranslation();

  const [selectedCountry, setSelectedCountry] = useState(country || "US");
  const [countryCode, setCountryCode] = useState(() => {
    try {
      return "+" + getCountryCallingCode(country || "US");
    } catch {
      return "+1";
    }
  });
  const [openCountries, setOpenCountries] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [touched, setTouched] = useState(false);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Helper to validate phone number against country
  const validatePhone = (num, countryIso) => {
    if (!num || !num.trim()) {
      return !required;
    }
    const digits = num.replace(/\D/g, "");
    if (!digits) return false;
    try {
      if (isValidPhoneNumber(num, countryIso)) {
        return true;
      }
      const code = getCountryCallingCode(countryIso);
      return isValidPhoneNumber(`+${code}${digits}`);
    } catch {
      return false;
    }
  };

  // Sync when country prop changes
  useEffect(() => {
    if (country) {
      setSelectedCountry(country);
      try {
        setCountryCode("+" + getCountryCallingCode(country));
      } catch (err) {
        console.warn("Invalid country code passed to PhoneField:", country);
      }
    }
  }, [country]);

  // Auto-detect country by IP on mount only if country prop is not provided
  useEffect(() => {
    if (country) return;
    const detectCountry = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();
        if (data?.country_code) {
          const code = "+" + getCountryCallingCode(data.country_code);
          setSelectedCountry(data.country_code);
          setCountryCode(code);
          onCountryChange?.(data.country_code, code);
        }
      } catch (err) {
        console.warn("Country detection failed:", err);
      }
    };
    detectCountry();
  }, [country]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!openCountries) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenCountries(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openCountries]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (openCountries) {
      setSearchQuery("");
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [openCountries]);

  // Localized country catalog sorted by name
  const countryList = useMemo(() => {
    let regionNames = null;
    try {
      regionNames = new Intl.DisplayNames([i18n.language || "en"], {
        type: "region",
      });
    } catch {
      /* ignore */
    }
    return getCountries()
      .map((code) => ({
        code,
        name: regionNames?.of(code) || code,
        callingCode: getCountryCallingCode(code),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [i18n.language]);

  // Filtered by search query (name, ISO code, or calling code)
  const filteredCountries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase().replace(/^\+/, "");
    if (!q) return countryList;
    return countryList.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.callingCode.includes(q),
    );
  }, [countryList, searchQuery]);

  const handleSelectCountry = (c) => {
    const code = "+" + getCountryCallingCode(c);
    setSelectedCountry(c);
    setCountryCode(code);
    setOpenCountries(false);
    onCountryChange?.(c, code);

    if (phone) {
      const valid = validatePhone(phone, c);
      const digits = phone.replace(/\D/g, "");
      const e164 = digits ? `${code}${digits}` : "";
      onPhoneChange?.(phone, valid, e164);
    }
  };

  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/[^\d\s\-()]/g, "");
    const formatted = new AsYouType(selectedCountry).input(raw);
    const valid = validatePhone(formatted, selectedCountry);
    const code = getCountryCallingCode(selectedCountry);
    const digits = formatted.replace(/\D/g, "");
    const e164 = digits ? `+${code}${digits}` : "";
    onPhoneChange?.(formatted, valid, e164);
  };

  const isCurrentlyValid = useMemo(() => {
    return validatePhone(phone, selectedCountry);
  }, [phone, selectedCountry, required]);

  const displayError =
    error ||
    (touched && phone && !isCurrentlyValid ? t("invalidPhoneNumber") : "");

  const FlagIcon = flags[selectedCountry] || flags.US;

  return (
    <div
      ref={containerRef}
      className={`relative flex w-full flex-col gap-2 ${className}`}
    >
      {label !== false && (
        <label className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center">
          {label || t("phoneNumber")}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {/* Input container */}
      <div
        className={`relative flex ${containerHeight} w-full items-center rounded-2xl border bg-white p-1 transition-all shadow-sm focus-within:ring-2 ${
          disabled ? "opacity-60 pointer-events-none" : ""
        } ${
          displayError
            ? "border-red-500 focus-within:border-red-500 focus-within:ring-red-200"
            : "border-gray-200 focus-within:border-[#020637] focus-within:ring-[#020637]/20"
        }`}
      >
        {/* Country prefix button */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpenCountries((prev) => !prev)}
          className="flex h-full items-center gap-1.5 rounded-xl bg-gray-100 hover:bg-gray-200/80 px-2.5 text-gray-800 transition-colors cursor-pointer select-none shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <FlagIcon style={{ width: 22, height: 15, borderRadius: 2 }} />
          <span className="text-xs sm:text-sm font-medium tabular-nums">
            {countryCode}
          </span>
          <ChevronDown
            className={`w-3.5 h-3.5 text-gray-500 transition-transform duration-200 ${
              openCountries ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Phone number input */}
        <input
          type="tel"
          disabled={disabled}
          autoFocus={autoFocus}
          value={phone || ""}
          placeholder={
            placeholder ||
            t("settings.enterPhoneNumber") ||
            t("phoneNumber")
          }
          onChange={handleInputChange}
          onKeyDown={onKeyDown}
          onBlur={() => setTouched(true)}
          className="h-full min-w-0 flex-1 bg-transparent px-3 text-sm text-gray-800 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
        />

        {/* Dropdown with search */}
        {openCountries && (
          <div className="absolute left-0 top-[calc(100%+6px)] z-50 w-full min-w-[280px] max-h-60 overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-2xl py-1">
            <div
              className="sticky top-0 bg-white px-3 py-2 border-b border-gray-100 z-10 flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                placeholder={t("search") || "Search..."}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full text-xs outline-none bg-transparent text-gray-800 placeholder:text-gray-400"
              />
            </div>

            {filteredCountries.length === 0 ? (
              <div className="py-4 text-center text-xs text-gray-400">
                {t("noResults") || "No results found"}
              </div>
            ) : (
              filteredCountries.map((c) => {
                const ItemFlag = flags[c.code] || flags.US;
                const isSelected = c.code === selectedCountry;
                return (
                  <div
                    key={c.code}
                    onClick={() => handleSelectCountry(c.code)}
                    className={`flex items-center gap-2.5 px-3 py-2 text-xs sm:text-sm cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-[#020637]/5 text-[#020637] font-medium"
                        : "text-gray-800 hover:bg-gray-50"
                    }`}
                  >
                    <ItemFlag
                      style={{ width: 20, height: 14, borderRadius: 2 }}
                    />
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-xs font-medium text-gray-400 tabular-nums">
                      +{c.callingCode}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {displayError && <span className="text-xs text-red-500">{displayError}</span>}
    </div>
  );
}
